import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { PenLine, Megaphone, Linkedin, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import UpgradeGate from "@/components/brand/UpgradeGate";
import ToolShell, { Composer } from "@/components/brand/ToolShell";
import { ChatBubble, UserText, AssistantMarkdown } from "@/components/brand/ChatMarkdown";

const FORMATS = [
  { value: "Social Media", label: "Social Media" },
  { value: "Newsletter", label: "Newsletter" },
  { value: "Website Copy", label: "Website Copy" },
  { value: "Email", label: "Email" },
  { value: "Other", label: "Other" },
];

const EMOTIONS = [
  { value: "Inspirerend", label: "Inspirerend" },
  { value: "Confronterend", label: "Confronterend" },
  { value: "Informerend", label: "Informerend" },
  { value: "Humoristisch", label: "Humoristisch" },
  { value: "Neutraal", label: "Neutraal" },
];

const QUICK_ACTIONS = [
  { title: "Welkomst e-mail", format: "Email", prompt: "Schrijf een korte, warme welkomst-e-mail voor een nieuwe klant.", Icon: Mail },
  { title: "LinkedIn thought leadership", format: "Social Media", prompt: "Schrijf een LinkedIn post van ~120 woorden waarin ik mijn visie deel op onze markt.", Icon: Linkedin },
  { title: "Scherpe advertentie", format: "Website Copy", prompt: "Schrijf een scherpe advertentietekst van 2 regels + call-to-action.", Icon: Megaphone },
];

export default function BrandWriter({ brand, role }) {
  return (
    <UpgradeGate brand={brand} role={role} feature="The Brand Writer">
      {({ refreshPlan }) => <WriterInner brand={brand} refreshPlan={refreshPlan} />}
    </UpgradeGate>
  );
}

function WriterInner({ brand, refreshPlan }) {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [format, setFormat] = useState("");
  const [emotion, setEmotion] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const streamingIdRef = useRef(null);

  useEffect(() => {
    setMessages([]);
    setSessionId(null);
  }, [brand.id]);

  const loadSession = async (sid) => {
    setSessionId(sid);
    setMessages([]);
    try {
      const { data } = await api.get(`/chat/history`, { params: { brand_id: brand.id, tool: "writer", session_id: sid } });
      const parsed = data.map((m, i) => ({ ...m, id: `${sid}_${i}` }));
      setMessages(parsed);
    } catch { toast.error("Chat kon niet geladen worden"); }
  };

  const newChat = () => { setSessionId(null); setMessages([]); setInput(""); };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const activeSid = sessionId || `writer_${brand.id}_${user?.user_id}_${Date.now().toString(36)}`;
    if (!sessionId) setSessionId(activeSid);
    setInput("");
    const uid = Date.now() + "u";
    const aid = Date.now() + "a";
    streamingIdRef.current = aid;
    const displayFormat = format || "—";
    const displayEmotion = emotion || "—";
    setMessages((m) => [
      ...m,
      { role: "user", content: text, format: displayFormat, emotion: displayEmotion, id: uid },
      { role: "assistant", content: "", id: aid, streaming: true },
    ]);
    setBusy(true);
    try {
      const token = localStorage.getItem("bh_token");
      const res = await fetch(`${API}/chat/writer`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          brand_id: brand.id,
          message: text,
          format: format || "Other",
          emotion: emotion || "",
          topic: text,
          session_id: activeSid,
        }),
      });
      if (!res.ok) {
        if (res.status === 402) {
          toast.error("Geen credits meer — upgrade voor onbeperkt gebruik");
          refreshPlan?.();
          setMessages((m) => m.filter((x) => x.id !== uid && x.id !== aid));
          return;
        }
        throw new Error("stream failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              acc += obj.delta;
              setMessages((m) => m.map((x) => (x.id === aid ? { ...x, content: acc } : x)));
            }
          } catch { /* ignore */ }
        }
      }
      setMessages((m) => m.map((x) => (x.id === aid ? { ...x, streaming: false } : x)));
      setRefreshKey((k) => k + 1);
    } catch {
      toast.error("Schrijven mislukt");
    } finally {
      setBusy(false);
      refreshPlan?.();
    }
  };

  const firstName = (user?.name || user?.email || "").split(" ")[0] || "there";
  const isEmpty = messages.length === 0;

  const emptyState = (
    <>
      <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 text-center">
        Hi {firstName},
      </h1>
      <p className="mt-3 text-lg text-slate-500 text-center max-w-xl mx-auto">
        I write copy in the voice of {brand.name}. Pick a format and emotion, then tell me what you need.
      </p>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {QUICK_ACTIONS.map(({ title, prompt, format: f, Icon }) => (
          <button
            key={title}
            onClick={() => { setFormat(f); setInput(prompt); }}
            className="group aspect-square rounded-xl border border-slate-200 bg-white hover:shadow-sm hover:border-slate-300 transition p-5 flex flex-col items-start justify-between text-left"
            data-testid={`writer-quick-${title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center">
              <Icon className="w-4 h-4 text-slate-700" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-900">{title}</div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-2">{prompt}</div>
            </div>
          </button>
        ))}
      </div>
    </>
  );

  return (
    <ToolShell
      brand={brand}
      tool="writer"
      title="The Brand Writer"
      Icon={PenLine}
      sessionId={sessionId}
      onLoadSession={loadSession}
      onNewChat={newChat}
      refreshKey={refreshKey}
      emptyState={emptyState}
      isEmpty={isEmpty}
      composer={
        <>
          <div className="rounded-2xl shadow-lg border border-slate-200 bg-white p-2">
            <ParamRow
              label="Format"
              options={FORMATS}
              value={format}
              onChange={setFormat}
              testIdPrefix="writer-format"
            />
            <ParamRow
              label="Emotion"
              options={EMOTIONS}
              value={emotion}
              onChange={setEmotion}
              testIdPrefix="writer-emotion"
            />
            <Composer
              value={input}
              onChange={setInput}
              onSend={send}
              busy={busy}
              placeholder="Describe what you want to write…"
            />
          </div>
          <div className="text-[11px] text-slate-400 text-center mt-2">
            Writer gebruikt alleen jouw <strong>Tone of voice</strong>-pagina als bron.
          </div>
        </>
      }
    >
      {messages.map((m) => (
        <ChatBubble key={m.id || m.created_at} role={m.role} streaming={m.streaming}>
          {m.role === "user" ? (
            <>
              {(m.format || m.emotion) && (
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1 flex gap-2">
                  {m.format && m.format !== "—" && <span>{m.format}</span>}
                  {m.emotion && m.emotion !== "—" && <span>· {m.emotion}</span>}
                </div>
              )}
              <UserText>{m.content}</UserText>
            </>
          ) : (
            <AssistantMarkdown content={m.content} />
          )}
        </ChatBubble>
      ))}
    </ToolShell>
  );
}

/** Horizontal scrollable row of pill toggles with a leading label. Empty value clears the selection. */
function ParamRow({ label, options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2 px-2 pt-1.5 pb-1 border-b border-slate-100">
      <span className="text-[10px] uppercase tracking-widest text-slate-400 shrink-0 w-16">{label}</span>
      <div className="flex flex-nowrap gap-1.5 overflow-x-auto bh-scroll pr-2">
        {options.map((o) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(active ? "" : o.value)}
              data-testid={`${testIdPrefix}-${o.value.toLowerCase().replace(/\s+/g, "-")}`}
              className={`shrink-0 h-7 px-2.5 rounded-full text-xs font-medium border transition ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
